/**
 * CancelSubscriptionButton Component (TASK-061)
 *
 * Destructive button with AlertDialog confirmation for cancelling
 * a subscription. Shows success/error toast notifications.
 */
import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { subscriptionApi } from '@/services/api/subscriptions';

interface CancelSubscriptionButtonProps {
  subscriptionId: string;
}

/**
 * Success toast component
 */
function SuccessToast({ message }: { message: string }) {
  return (
    <div
      data-testid="success-toast"
      className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50"
    >
      {message}
    </div>
  );
}

/**
 * Error toast component
 */
function ErrorToast({ message }: { message: string }) {
  return (
    <div
      data-testid="error-toast"
      className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg z-50"
    >
      {message}
    </div>
  );
}

export function CancelSubscriptionButton({
  subscriptionId,
}: CancelSubscriptionButtonProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: () => subscriptionApi.cancel(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionUsage'] });
      setIsOpen(false);
      setShowSuccessToast(true);
      // Note: setTimeout is not cleaned up on unmount. This follows the existing
      // project pattern (e.g. SettingsPage.tsx). Ideally, useEffect with cleanup
      // would be used, but kept consistent with the rest of the codebase.
      setTimeout(() => setShowSuccessToast(false), 3000);
    },
    onError: () => {
      setShowErrorToast(true);
      // Note: Same setTimeout pattern as onSuccess - see comment above.
      setTimeout(() => setShowErrorToast(false), 3000);
    },
  });

  const handleConfirm = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      cancelMutation.mutate();
    },
    [cancelMutation]
  );

  return (
    <>
      {!isOpen && (
        <Button
          data-testid="cancel-subscription-trigger"
          data-variant="destructive"
          variant="destructive"
          onClick={() => setIsOpen(true)}
        >
          Отменить подписку
        </Button>
      )}

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent data-testid="cancel-subscription-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить подписку</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены? После отмены вы потеряете доступ к расширенным
              функциям по окончании текущего оплаченного периода.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="dismiss-cancel-button"
            >
              Отмена
            </AlertDialogCancel>
            <Button
              data-testid="confirm-cancel-button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Отмена...' : 'Подтвердить отмену'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showSuccessToast && (
        <SuccessToast message="Подписка успешно отменена" />
      )}
      {showErrorToast && (
        <ErrorToast message="Не удалось отменить подписку" />
      )}
    </>
  );
}
